"use client";

import { useState, useEffect, ChangeEvent, FormEvent, useCallback } from "react";
import Button from "../../components/ui/button/Button";
import { Modal } from "../../components/ui/modal";
import { Table, TableHeader, TableBody, TableRow, TableCell } from "../../components/ui/table";
import Badge from "../../components/ui/badge/Badge";
import ComponentCard from "../../components/common/ComponentCard";
import type { ReservaB2B, AgenciaB2B, Excursion, NoShow, ReservationStatus } from "../../hooks/useApi";
import useApi from "../../hooks/useApi";
import CreateAgencyModal from "../../components/agencies/CreateAgencyModal";
import { toast } from "react-hot-toast";
import * as XLSX from 'xlsx';
import Input from "../../components/form/input/InputField";
import useAuth from "../../hooks/useAuth";
import RoleBasedRenderer from "../../components/auth/RoleBasedRenderer";
import { Calendar, DollarSign, FileText, User, Users, AlertCircle, ClipboardList, CreditCard, TrendingUp, Tag, Info, FileCheck, Building, Building2, Search, Eye, Edit, Trash2 } from 'lucide-react';
import ReservaDetailsModal from "../../components/reservas/ReservaDetailsModal";
import DuplicateReservationCheck from "../../components/reservas/DuplicateReservationCheck";

// Helper: convierte "dd/mm/yyyy" a "yyyy-mm-dd" para compatibilidad con DatePicker
function formatDateForInput(dateStr: string): string {
  if (!dateStr) return "";
  
  // If already in ISO format, return as is
  if (dateStr.match(/^\d{4}-\d{2}-\d{2}(T|$)/)) {
    return dateStr.split('T')[0];
  }
  
  // Handle dd/mm/yyyy format
  const parts = dateStr.split("/");
  if (parts.length !== 3) return "";
  const [day, month, year] = parts;
  return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
}

type BadgeColor = "primary" | "success" | "error" | "warning" | "info" | "light" | "dark";

// Mapeo de estados para presentación amigable
const estadoDisplay = {
  pending_payment: "Pendiente",
  partial_payment: "Pago parcial",
  paid: "Pagada",
  no_show: "No Show",
  completed: "Completada",
};

interface B2BReservasProps {
  reservas: ReservaB2B[];
  searchTerm: string;
  dateFilter: string;
  thirdPartyFilter: boolean | null;
  onSearchChange: (value: string) => void;
  onDateFilterChange: (value: string) => void;
  onThirdPartyFilterChange: (value: boolean | null) => void;
  onAddReserva: (newReserva: Omit<ReservaB2B, "id">) => Promise<void>;
  onEditReserva: (updatedReserva: ReservaB2B) => void;
  onDeleteReserva: (id: number) => void;
}

interface ReservaFormData {
  agencia: string;
  excursion: string;
  fecha: string;
  adultos: string;
  ninios: string;
  hotel: string;
  metodoPago: "efectivo" | "tarjeta" | "transferencia";
  notas: string;
  descuento: string;
  montoFacturado: string;
  estado: string;
  taxRate: string;
  clientEndName: string;
  agenciaComision: string;
  incluyeSeguro: boolean;
}

export default function B2BReservas({
  reservas,
  searchTerm,
  dateFilter,
  thirdPartyFilter,
  onSearchChange,
  onDateFilterChange,
  onThirdPartyFilterChange,
  onAddReserva,
  onEditReserva,
  onDeleteReserva,
}: B2BReservasProps) {
  const { getAgencies, getExcursiones, getHotels, calculatePrice, registerPayment, createAgency, registerNoShow, getReservationNoShows, deleteNoShow, getReservas } = useApi();
  const { canCreate, canEdit, canDelete } = useAuth();

  // Create stable references to API functions
  const stableGetAgencies = useCallback(getAgencies, []);
  const stableGetExcursiones = useCallback(getExcursiones, []);
  const stableGetHotels = useCallback(getHotels, []);

  // Get tomorrow's date in YYYY-MM-DD format
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowStr = tomorrow.toISOString().split('T')[0];

  // Estados para formulario (creación/edición)
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [editingReserva, setEditingReserva] = useState<ReservaB2B | null>(null);
  const [reservaFormData, setReservaFormData] = useState<ReservaFormData>({
    agencia: "",
    excursion: "",
    fecha: "",
    adultos: "1",
    ninios: "0",
    hotel: "",
    metodoPago: "efectivo",
    notas: "",
    descuento: "0",
    montoFacturado: "0",
    estado: "pending_payment",
    taxRate: "0",
    clientEndName: "",
    agenciaComision: "",
    incluyeSeguro: false,
  });

  const [calculatedPricePreview, setCalculatedPricePreview] = useState<number | null>(null);
  const [isCalculatingPrice, setIsCalculatingPrice] = useState(false);
  const [justificacionSobrepago, setJustificacionSobrepago] = useState("");

  const [agencies, setAgencies] = useState<AgenciaB2B[]>([]);
  const [excursions, setExcursions] = useState<Excursion[]>([]);
  const [hotels, setHotels] = useState<string[]>([]);

  // Modal de detalles
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedReserva, setSelectedReserva] = useState<ReservaB2B | null>(null);

  // Modal de confirmación para eliminación
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [reservationIdToDelete, setReservationIdToDelete] = useState<number | null>(null);

  // Modal para registrar pago adicional
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [selectedReservaForPayment, setSelectedReservaForPayment] = useState<ReservaB2B | null>(null);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState<"efectivo" | "tarjeta" | "transferencia">("efectivo");
  const [justificacionExtra, setJustificacionExtra] = useState("");

  // Agregar el estado para el modal
  const [isNewAgencyModalOpen, setIsNewAgencyModalOpen] = useState(false);

  const today = new Date().toISOString().split("T")[0];

  // Add a new state variable to track if a custom price is being used
  const [hasCustomPrice, setHasCustomPrice] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Add pagination state variables after other state declarations
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10; // Number of reservations to show per page

  // Add useEffect to reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, dateFilter, thirdPartyFilter]);

  // Al editar, precargamos datos: agencia, montoFacturado y estado
  useEffect(() => {
    if (editingReserva && agencies.length > 0) {
      const agencyId = editingReserva.agencia ?? (editingReserva as any).agencyId;
      setReservaFormData((prev) => ({
        ...prev,
        agencia: agencyId ? agencyId.toString() : "",
        montoFacturado: editingReserva.montoFacturado.toString(),
        estado: editingReserva.estado,
      }));
    }
  }, [editingReserva, agencies]);

  useEffect(() => {
    async function fetchDropdownData() {
      try {
        const agenciesData = await stableGetAgencies();
        setAgencies(agenciesData);
      } catch (error) {
        toast.error("Error obteniendo agencias");
      }
      try {
        const excursionsData = await stableGetExcursiones();
        setExcursions(excursionsData);
      } catch (error) {
        toast.error("Error obteniendo excursiones");
      }
      try {
        const hotelsData = await stableGetHotels();
        setHotels(hotelsData);
      } catch (error) {
        toast.error("Error obteniendo hoteles");
      }
    }
    fetchDropdownData();
  }, [stableGetAgencies, stableGetExcursiones, stableGetHotels]);

  const handleOpenAddModal = () => {
    setReservaFormData({
      agencia: "",
      excursion: "",
      fecha: "",
      adultos: "1",
      ninios: "0",
      hotel: "",
      metodoPago: "efectivo",
      notas: "",
      descuento: "0",
      montoFacturado: "0",
      estado: "pending_payment",
      taxRate: "0",
      clientEndName: "",
      agenciaComision: "",
      incluyeSeguro: false,
    });
    setCalculatedPricePreview(null);
    setEditingReserva(null);
    setIsFormModalOpen(true);
  };

  const handleOpenEditModal = (reserva: ReservaB2B) => {
    setReservaFormData({
      agencia: reserva.agencyId?.toString() || reserva.agencia.toString(),
      excursion: reserva.excursion,
      fecha: formatDateForInput(reserva.fechaISO || reserva.fecha),
      adultos: reserva.adultos.toString(),
      ninios: reserva.ninios.toString(),
      hotel: reserva.hotel,
      metodoPago: reserva.metodoPago || "efectivo",
      notas: reserva.notas || "",
      descuento: reserva.descuento?.toString() || "0",
      montoFacturado: reserva.montoFacturado?.toString() || "0",
      estado: reserva.estado,
      taxRate: reserva.taxRate?.toString() || "0",
      clientEndName: reserva.clientEndName || "",
      agenciaComision: reserva.agenciaComision !== undefined ? reserva.agenciaComision.toString() : "",
      incluyeSeguro: reserva.incluyeSeguro ?? false
    });
    setCalculatedPricePreview(null);
    setEditingReserva(reserva);
    setIsFormModalOpen(true);
  };

  const handleOpenDetailModal = async (reserva: ReservaB2B) => {
    setSelectedReserva(reserva);
    setIsDetailModalOpen(true);
    
    // Fetch no shows for this reservation
    try {
      const fetchedNoShows = await getReservationNoShows(reserva.id);
      setNoShows(fetchedNoShows);
    } catch (error) {
      console.error("Error fetching no shows:", error);
      toast.error("Error al cargar los registros de No Show");
    }
  };

  // Para registrar pago adicional si la reserva tiene saldo pendiente y no está en "no_show"
  const handleOpenPaymentModal = (reserva: ReservaB2B) => {
    if (reserva.estado === "no_show" || reserva.saldoPendiente <= 0) return;
    setSelectedReservaForPayment(reserva);
    setPaymentAmount("");
    setPaymentMethod("efectivo");
    setJustificacionExtra("");
    setIsPaymentModalOpen(true);
  };

  // Update the calculatePricePreview function to handle response properly
  const calculatePricePreview = useCallback(async () => {
    const { excursion, adultos, ninios, descuento, taxRate, agencia, agenciaComision } = reservaFormData;
    
    // Only calculate if we have all necessary values
    if (!excursion || !adultos || adultos === "0") {
      setCalculatedPricePreview(null);
      setHasCustomPrice(false);
      return;
    }
    
    try {
      setIsCalculatingPrice(true);
      
      // Log calculation parameters for debugging
      console.log('Calculating B2B price preview:', {
        type: "b2b",
        excursion,
        adultos: Number(adultos),
        ninios: Number(ninios || "0"),
        descuento: Number(descuento || "0"),
        taxRate: Number(taxRate || "0"),
        agencia: agencia ? Number(agencia) : undefined,
        agenciaComision: Number(agenciaComision || "0")
      });
      
      const response = await calculatePrice({
        type: "b2b",
        excursion,
        adultos: Number(adultos),
        ninios: Number(ninios || "0"),
        descuento: Number(descuento || "0"),
        taxRate: Number(taxRate || "0"),
        agencia: agencia ? Number(agencia) : undefined,
        agenciaComision: Number(agenciaComision || "0"),
        incluyeSeguro: Boolean(reservaFormData.incluyeSeguro)
      });
      
      // If the response has details about custom pricing, extract it
      if (typeof response === 'object' && 'price' in response) {
        setCalculatedPricePreview(response.price);
        setHasCustomPrice(response.details?.hasCustomPrice || false);
        
        // Log if custom pricing was detected
        if (response.details?.hasCustomPrice) {
          console.log('Custom pricing detected for B2B reservation:', response.details);
        }
      } else {
        // Otherwise just use the price (backward compatibility)
        setCalculatedPricePreview(response as number);
        setHasCustomPrice(false);
      }
    } catch (error) {
      console.error("Error en el cálculo de precio previo:", error);
      setCalculatedPricePreview(null);
      setHasCustomPrice(false);
    } finally {
      setIsCalculatingPrice(false);
    }
  }, [reservaFormData, calculatePrice]);

  // Trigger price calculation when form values change that affect price
  useEffect(() => {
    if (isFormModalOpen) {
      const debouncedCalculation = setTimeout(() => {
        calculatePricePreview();
      }, 500); // Debounce to avoid too many API calls
      
      return () => clearTimeout(debouncedCalculation);
    }
  }, [
    isFormModalOpen,
    reservaFormData.excursion,
    reservaFormData.adultos, 
    reservaFormData.ninios,
    reservaFormData.descuento,
    reservaFormData.taxRate,
    reservaFormData.agencia,
    reservaFormData.agenciaComision, // Recalcular también cuando cambie la comisión
    calculatePricePreview
  ]);

  // Reset justification when form data changes
  useEffect(() => {
    // Check if we need to clear the justification (when montoFacturado is no longer > calculatedPricePreview)
    const montoFacturado = parseFloat(reservaFormData.montoFacturado || "0");
    if (calculatedPricePreview === null || montoFacturado <= calculatedPricePreview) {
      setJustificacionSobrepago("");
    }
  }, [reservaFormData.montoFacturado, calculatedPricePreview]);

  const handleSaveReserva = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Validar datos del formulario
    if (
      !reservaFormData.agencia ||
      !reservaFormData.excursion ||
      !reservaFormData.fecha ||
      !reservaFormData.adultos ||
      !reservaFormData.hotel
    ) {
      toast.error("Por favor completa todos los campos requeridos");
      setIsSubmitting(false);
      return;
    }

    try {
      const numAdultos = parseInt(reservaFormData.adultos);
      const numNinios = parseInt(reservaFormData.ninios || "0");
      const numDescuento = parseFloat(reservaFormData.descuento || "0");
      const taxRateValue = parseFloat(reservaFormData.taxRate || "18");
      const montoFacturado = parseFloat(reservaFormData.montoFacturado || "0");
      const agenciaComision = parseFloat(reservaFormData.agenciaComision || "0");

      // Obtener el precio calculado de la API
      const priceResponse = await calculatePrice({
        type: "b2b",
        excursion: reservaFormData.excursion,
        adultos: numAdultos,
        ninios: numNinios,
        descuento: numDescuento,
        taxRate: taxRateValue,
        agencia: parseInt(reservaFormData.agencia),
        agenciaComision: agenciaComision,
        incluyeSeguro: reservaFormData.incluyeSeguro
      });

      // Extraer el precio total de la respuesta (que puede ser un objeto o directamente un número)
      const precioTotal = typeof priceResponse === 'object' && 'price' in priceResponse 
        ? priceResponse.price 
        : priceResponse as number;

      // Check if montoFacturado exceeds precioTotal and justification is required
      if (montoFacturado > precioTotal && !justificacionSobrepago.trim()) {
        toast.error("Debe proporcionar una justificación cuando el pago inicial excede el precio total");
        setIsSubmitting(false);
        return;
      }

      if (editingReserva) {
        // Actualizar reserva existente
        if (selectedReserva) {
          const updatedReserva: ReservaB2B = {
            ...selectedReserva,
            agencia: parseInt(reservaFormData.agencia),
            excursion: reservaFormData.excursion,
            fecha: reservaFormData.fecha,
            adultos: numAdultos,
            ninios: numNinios,
            hotel: reservaFormData.hotel,
            metodoPago: reservaFormData.metodoPago,
            notas: reservaFormData.notas,
            descuento: numDescuento,
            montoFacturado: montoFacturado,
            precioTotal: precioTotal || selectedReserva.precioTotal,
            saldoPendiente:
              (precioTotal || selectedReserva.precioTotal) -
              montoFacturado,
            estado:
              (precioTotal || selectedReserva.precioTotal) <=
              montoFacturado
                ? "paid"
                : montoFacturado > 0
                ? "partial_payment"
                : "pending_payment",
            taxRate: Number(reservaFormData.taxRate),
            clientEndName: reservaFormData.clientEndName,
            agenciaComision: agenciaComision,
            incluyeSeguro: reservaFormData.incluyeSeguro,
          };

          await onEditReserva(updatedReserva);
          setIsFormModalOpen(false);
          toast.success("Reserva actualizada correctamente");
          return;
        }
      } else {
        // Crear nueva reserva
        const newReserva: Omit<ReservaB2B, "id"> = {
          type: "b2b",
          agencia: parseInt(reservaFormData.agencia),
          excursion: reservaFormData.excursion,
          fecha: reservaFormData.fecha,
          personas: numAdultos + numNinios,
          adultos: numAdultos,
          ninios: numNinios,
          hotel: reservaFormData.hotel,
          precioTotal: precioTotal || 0,
          metodoPago: reservaFormData.metodoPago,
          notas: reservaFormData.notas,
          descuento: numDescuento,
          montoFacturado: montoFacturado,
          saldoPendiente: montoFacturado >= precioTotal ? 0 : (precioTotal || 0) - montoFacturado,
          estado:
            precioTotal <= montoFacturado
              ? "paid"
              : montoFacturado > 0
              ? "partial_payment"
              : "pending_payment",
          taxRate: Number(reservaFormData.taxRate),
          clientEndName: reservaFormData.clientEndName,
          agenciaComision: agenciaComision,
          incluyeSeguro: reservaFormData.incluyeSeguro,
          // Add manual adjustment properties when needed
          ...(montoFacturado > precioTotal ? {
            hasManualAdjustment: true,
            adjustmentReason: justificacionSobrepago
          } : {})
        };

        await onAddReserva(newReserva);
        setIsFormModalOpen(false);
        toast.success("Reserva creada correctamente");
      }

      setReservaFormData({
        agencia: "",
        excursion: "",
        fecha: "",
        adultos: "",
        ninios: "",
        hotel: "",
        metodoPago: "efectivo",
        notas: "",
        descuento: "",
        montoFacturado: "",
        estado: "",
        taxRate: "0",
        clientEndName: "",
        agenciaComision: "",
        incluyeSeguro: false,
      });
      setJustificacionSobrepago(""); // Reset justification field
    } catch (error) {
      toast.error("Error al procesar la reserva");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRegisterPayment = async () => {
    if (!selectedReservaForPayment) return;
    const amount = Number(paymentAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error("Ingrese un monto de pago válido");
      return;
    }
    
    // Check if it's an overpayment
    const isOverpayment = amount > selectedReservaForPayment.saldoPendiente;
    
    // If it's an overpayment and no justification provided, show error
    if (isOverpayment && !justificacionExtra.trim()) {
      toast.error("Para pagos mayores al saldo pendiente, debe proporcionar una justificación");
      return;
    }
    
    try {
      // Include justification if it's an overpayment
      const updatedReserva = await registerPayment(
        selectedReservaForPayment.id, 
        amount, 
        paymentMethod,
        isOverpayment ? justificacionExtra : undefined
      );
      onEditReserva(updatedReserva as ReservaB2B);
      setIsPaymentModalOpen(false);
      toast.success("Pago registrado correctamente");
    } catch (error) {
      toast.error("Error al registrar el pago");
    }
  };

  // Función para obtener agencias únicas
  const getUniqueAgenciesFromReservations = () => {
    const uniqueAgencies = new Map();
    reservas.forEach((res) => {
      if (res.agenciaName) {
        uniqueAgencies.set(res.agenciaName, {
          id: res.agencia,
          name: res.agenciaName
        });
      }
    });
    return Array.from(uniqueAgencies.values())
      .sort((a, b) => a.name.localeCompare(b.name)); // Ordenar alfabéticamente
  };

  // Función para obtener hoteles únicos
  const getUniqueHotelsFromReservations = () => {
    const uniqueHotels = new Set();
    reservas.forEach(reserva => {
      if (reserva.hotel) {
        uniqueHotels.add(reserva.hotel);
      }
    });
    return Array.from(uniqueHotels) as string[];
  };

  // Filtrar reservas según los criterios aplicados
  const filteredReservas = reservas.filter(reserva => {
    // 1. Agencia filter - check if the agency name contains the search term (partial match)
    const normalizedAgencyName = reserva.agenciaName?.toLowerCase().trim() || "";
    const normalizedSearchTerm = searchTerm.toLowerCase().trim();
    const matchesAgency = !normalizedSearchTerm || normalizedAgencyName.includes(normalizedSearchTerm);

    // 2. Date filter
    const reservaDate = reserva.fechaISO || formatDateForInput(reserva.fecha);
    const matchesDate = !dateFilter || reservaDate === dateFilter;

    // 3. Third party filter
    const isThirdParty = Boolean(reserva.isThirdParty);
    const matchesThirdParty = thirdPartyFilter === null || isThirdParty === thirdPartyFilter;

    return matchesAgency && matchesDate && matchesThirdParty;
  });

  // Add a function to reset filters
  const resetFilters = () => {
    onSearchChange("");
    onDateFilterChange("");
    onThirdPartyFilterChange(null);
  };

  const handleExportToExcel = () => {
    // Prepare the data for export - using filtered reservas
    const exportData = filteredReservas.map(res => ({
      'Agencia': res.agenciaName || '',
      'Cliente Final': res.clientEndName || '',
      'Excursión': res.excursion,
      'Fecha': res.fecha,
      'Adultos': res.adultos,
      'Niños': res.ninios,
      'Total Personas': res.personas,
      'Hotel': res.hotel || '',
      'Método de Pago': res.metodoPago,
      'Precio Total': res.precioTotal,
      'Monto Facturado': res.montoFacturado,
      'Saldo Pendiente': res.saldoPendiente,
      'Estado': estadoDisplay[res.estado],
      'Notas': res.notas || '',
      'Terceros': res.isThirdParty ? 'Terceros' : 'Propia'
    }));

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Reservas B2B');

    // Generate file name with current date and filter info
    const date = new Date().toISOString().split('T')[0];
    let fileName = `reservas_b2b_${date}`;
    
    // Add filter information to filename
    if (searchTerm) {
      fileName += `_agencia_${searchTerm.replace(/\s+/g, '_')}`;
    }
    if (dateFilter) {
      fileName += `_fecha_${dateFilter}`;
    }
    if (thirdPartyFilter !== null) {
      fileName += thirdPartyFilter ? '_terceros' : '_propias';
    }
    
    fileName += '.xlsx';

    // Save the file
    XLSX.writeFile(wb, fileName);
    toast.success(`Exportadas ${filteredReservas.length} reservas a Excel`);
  };

  function getStatusBadgeColor(status: string): BadgeColor {
    switch (status) {
      case "pending_payment":
        return "primary";
      case "partial_payment":
        return "info";
      case "paid":
        return "success";
      case "no_show":
        return "warning";
      case "completed":
        return "dark";
      default:
        return "light";
    }
  }

  // Add pagination control functions 
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Calculate pagination values after filterReservas is defined
  const totalPages = Math.ceil(filteredReservas.length / itemsPerPage);
  const paginatedReservas = filteredReservas.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const [noShows, setNoShows] = useState<NoShow[]>([]);
  const [userRole, setUserRole] = useState<string>("worker");

  // Get user role from localStorage when component mounts
  useEffect(() => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const payload = JSON.parse(window.atob(base64));
        
        if (payload.role) {
          setUserRole(payload.role);
        }
      }
    } catch (error) {
      console.error('Error getting user role:', error);
    }
  }, []);

  // Handle registering a new No Show
  const handleRegisterNoShow = async (data: {
    reservationId: number;
    missingAdults: number;
    missingChildren: number;
    justificacion?: string;
  }) => {
    try {
      const result = await registerNoShow(data);
      
      // If the entire reservation is marked as no-show, we need to refresh the main list
      if (result.reservationUpdated) {
        toast.success("La reserva ha sido marcada como No Show");
        
        // Refresh the selected reservation to show updated status
        if (selectedReserva) {
          try {
            // Refresh no shows
            const fetchedNoShows = await getReservationNoShows(data.reservationId);
            setNoShows(fetchedNoShows);
            
            // Update the local reservation status
            const updatedReserva = {
              ...selectedReserva,
              estado: 'no_show' as const
            };
            setSelectedReserva(updatedReserva);
            
            // Update the reservation in the parent component
            onEditReserva(updatedReserva);
          } catch (refreshError) {
            console.error("Error refreshing reservation data:", refreshError);
          }
        }
      } else {
        toast.success("No Show registrado correctamente");
        
        // Refresh no shows
        const fetchedNoShows = await getReservationNoShows(data.reservationId);
        setNoShows(fetchedNoShows);
      }
      
      return; // Return void to match the expected type
    } catch (error) {
      console.error("Error registering no show:", error);
      toast.error("Error al registrar el no show");
      throw error; // Re-throw to propagate to the modal
    }
  };
  
  // Handle deleting a No Show
  const handleDeleteNoShow = async (id: number) => {
    try {
      await deleteNoShow(id);
      toast.success("No Show eliminado correctamente");
      
      // Refresh the list of no shows if we have a selected reservation
      if (selectedReserva) {
        await refreshNoShows();
        
        // If this was the only no show and the reservation is marked as no_show,
        // we should update the reservation status
        if (noShows.length === 1 && selectedReserva.estado === "no_show") {
          // Determine the new status based on payment status
          let newStatus: ReservationStatus = "pending_payment";
          
          if (selectedReserva.saldoPendiente <= 0) {
            newStatus = "paid";
          } else if (selectedReserva.montoFacturado > 0) {
            newStatus = "partial_payment";
          }
          
          // Update local state
          const updatedReserva = {
            ...selectedReserva,
            estado: newStatus
          };
          
          setSelectedReserva(updatedReserva);
          
          // Update parent component
          onEditReserva(updatedReserva);
        }
      }
    } catch (error) {
      console.error("Error deleting no show:", error);
      toast.error("Error al eliminar el no show");
      throw error;
    }
  };
  
  // Refresh no shows when needed
  const refreshNoShows = async () => {
    if (selectedReserva) {
      try {
        const fetchedNoShows = await getReservationNoShows(selectedReserva.id);
        setNoShows(fetchedNoShows);
      } catch (error) {
        console.error("Error refreshing no shows:", error);
      }
    }
  };

  return (
    <div className="space-y-6">
      <ComponentCard className="mb-6" title="Filtros de búsqueda">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Input
              type="text"
              value={searchTerm}
              onChange={(e: ChangeEvent<HTMLInputElement>) => onSearchChange(e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 focus:border-blue-500 focus:ring-blue-500"
              placeholder="Buscar por nombre de agencia"
            />
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <Search className="h-4 w-4 text-gray-400 dark:text-gray-500" />
            </div>
          </div>
          <Input
            type="date"
            value={dateFilter}
            onChange={(e) => onDateFilterChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 focus:border-blue-500 focus:ring-blue-500"
            placeholder="Filtrar por fecha"
            dateFormat="yyyy-MM-dd"
          />
          <select
            value={thirdPartyFilter === null ? "all" : thirdPartyFilter ? "thirdParty" : "own"}
            onChange={(e) => {
              const value = e.target.value;
              const thirdPartyValue = value === "all" ? null : value === "thirdParty";
              onThirdPartyFilterChange(thirdPartyValue);
            }}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 focus:border-blue-500 focus:ring-blue-500"
          >
            <option value="all">Todas las excursiones</option>
            <option value="own">Excursiones propias</option>
            <option value="thirdParty">Excursiones de terceros</option>
          </select>
          
          {/* Buttons */}
          <div className="flex gap-2">
            <RoleBasedRenderer requiredPermission="create">
              <Button variant="primary" onClick={handleOpenAddModal} className="flex-1">
                Nueva reserva
              </Button>
            </RoleBasedRenderer>
            <Button variant="outline" onClick={handleExportToExcel} className="flex-1">
              Exportar Excel
            </Button>
          </div>
        </div>
      </ComponentCard>

      <ComponentCard title="Reservas B2B">
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-gray-100 dark:scrollbar-track-gray-800">
          <Table className="w-max divide-y divide-gray-200 dark:divide-gray-700">
            <TableHeader className="bg-gray-50 dark:bg-gray-800">
              <TableRow>
                <TableCell isHeader className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider sm:px-6">
                  Agencia
                </TableCell>
                <TableCell isHeader className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider sm:px-6">
                  Excursión
                </TableCell>
                <TableCell isHeader className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider sm:px-6">
                  Fecha
                </TableCell>
                <TableCell isHeader className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider sm:px-6">
                  Estado de Pago
                </TableCell>
                <TableCell isHeader className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider sm:px-6">
                  Acciones
                </TableCell>
              </TableRow>
            </TableHeader>
            <TableBody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
              {paginatedReservas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                    {searchTerm || dateFilter 
                      ? "No se encontraron reservas con los filtros aplicados" 
                      : "No hay reservas registradas"}
                  </TableCell>
                </TableRow>
              ) : (
                paginatedReservas.map((res) => (
                  <TableRow key={res.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                    <TableCell className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100 sm:px-6">
                      {res.agenciaName}
                    </TableCell>
                    <TableCell className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300 sm:px-6">
                      {res.excursion}
                    </TableCell>
                    <TableCell className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300 sm:px-6">
                      {res.fecha}
                    </TableCell>
                    <TableCell className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300 sm:px-6">
                      <Badge color={getStatusBadgeColor(res.estado)}>
                        {estadoDisplay[res.estado]}
                      </Badge>
                    </TableCell>
                    <TableCell className="px-4 py-4 whitespace-nowrap text-sm font-medium sm:px-6">
                      <div className="flex space-x-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="p-2"
                          aria-label="Ver detalles"
                          onClick={() => handleOpenDetailModal(res)}
                        >
                          <Eye size={16} />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="p-2"
                          aria-label="Editar reserva"
                          onClick={() => handleOpenEditModal(res)}
                        >
                          <Edit size={16} />
                        </Button>
                        <RoleBasedRenderer requiredPermission="delete">
                          <Button
                            variant="outline"
                            size="sm"
                            className="p-2"
                            aria-label="Eliminar reserva"
                            onClick={() => {
                              setReservationIdToDelete(res.id);
                              setIsDeleteModalOpen(true);
                            }}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </RoleBasedRenderer>
                        {res.estado !== "no_show" && res.saldoPendiente > 0 && (
                          <Button 
                            variant="primary" 
                            size="sm" 
                            className="p-2"
                            aria-label="Registrar pago"
                            onClick={() => handleOpenPaymentModal(res)}
                          >
                            <DollarSign size={16} />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </ComponentCard>

      {/* Add pagination controls after the table but before modals */}
      {filteredReservas.length > itemsPerPage && (
        <div className="flex items-center justify-between px-4 py-3 sm:px-6 border-t border-gray-200 dark:border-gray-700 mt-4">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              className={`relative inline-flex items-center px-4 py-2 text-sm font-medium rounded-md 
                ${currentPage === 1 
                  ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed' 
                  : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'} 
                border border-gray-300 dark:border-gray-600`}
            >
              Anterior
            </button>
            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className={`ml-3 relative inline-flex items-center px-4 py-2 text-sm font-medium rounded-md
                ${currentPage === totalPages 
                  ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed' 
                  : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'} 
                border border-gray-300 dark:border-gray-600`}
            >
              Siguiente
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Mostrando <span className="font-medium">{Math.min((currentPage - 1) * itemsPerPage + 1, filteredReservas.length)}</span> a{" "}
                <span className="font-medium">{Math.min(currentPage * itemsPerPage, filteredReservas.length)}</span> de{" "}
                <span className="font-medium">{filteredReservas.length}</span> reservas
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                  className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 text-sm font-medium
                    ${currentPage === 1 
                      ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed' 
                      : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                >
                  <span className="sr-only">Anterior</span>
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
                
                {/* Page numbers - limited to 5 pages */}
                {(() => {
                  // Calculate which page numbers to show (max 5)
                  let startPage = Math.max(1, currentPage - 2);
                  let endPage = Math.min(totalPages, startPage + 4);
                  
                  // Adjust startPage if we're near the end
                  if (endPage - startPage < 4 && startPage > 1) {
                    startPage = Math.max(1, endPage - 4);
                  }
                  
                  const pages = [];
                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <button
                        key={i}
                        onClick={() => setCurrentPage(i)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium
                          ${currentPage === i
                            ? 'z-10 bg-indigo-50 dark:bg-indigo-900/30 border-indigo-500 dark:border-indigo-500 text-indigo-600 dark:text-indigo-400'
                            : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                      >
                        {i}
                      </button>
                    );
                  }
                  return pages;
                })()}
                
                <button
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                  className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 text-sm font-medium
                    ${currentPage === totalPages 
                      ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed' 
                      : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                >
                  <span className="sr-only">Siguiente</span>
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Modal de creación/edición */}
      <Modal
        isOpen={isFormModalOpen}
        onClose={() => setIsFormModalOpen(false)}
        title={editingReserva ? "Editar reserva" : "Nueva reserva"}
      >
        <form
          onSubmit={handleSaveReserva}
          className="space-y-4"
        >
          <div className="space-y-4">
            {/* Agencia - Required */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label htmlFor="agencia" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  <Building className="w-4 h-4 inline-block mr-1.5 text-primary-500" />
                  Agencia
                  <span className="text-red-500">*</span>
                </label>
                <button
                  type="button"
                  onClick={() => setIsNewAgencyModalOpen(true)}
                  className="text-sm text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 focus:outline-none"
                >
                  + Crear nueva agencia
                </button>
              </div>
              <select
                id="agencia"
                value={reservaFormData.agencia}
                onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                  setReservaFormData({ ...reservaFormData, agencia: e.target.value })
                }
                className="h-11 w-full appearance-none rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 pr-11 text-sm shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
                required
              >
                <option value="">Seleccione una agencia</option>
                {agencies
                  .filter((agency) => agency.active)
                  .map((agency) => (
                    <option key={`${agency.id}-${agency.name}`} value={agency.id.toString()}>
                      {agency.name}
                    </option>
                  ))}
              </select>
            </div>

            {/* Cliente Final - Nuevo campo */}
            <div className="space-y-2">
              <label htmlFor="clientEndName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                <User className="w-4 h-4 inline-block mr-1.5 text-primary-500" />
                Nombre del cliente final
              </label>
              <Input
                id="clientEndName"
                type="text"
                name="clientEndName"
                value={reservaFormData.clientEndName}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setReservaFormData({ ...reservaFormData, clientEndName: e.target.value })
                }
                placeholder="Nombre del cliente final o grupo"
                className="w-full"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Nombre del cliente o grupo para quien la agencia realiza la reserva
              </p>
            </div>

            {/* Agencia Comisión - Nuevo campo */}
            <div className="mb-4">
              <label htmlFor="agenciaComision" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Comisión de Agencia ($)
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 sm:text-sm">$</span>
                </div>
                <input
                  type="number"
                  id="agenciaComision"
                  className="pl-7 mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
                  name="agenciaComision"
                  min="0"
                  step="0.01"
                  value={reservaFormData.agenciaComision}
                  onChange={(e) =>
                    setReservaFormData({ ...reservaFormData, agenciaComision: e.target.value })
                  }
                  placeholder="0.00"
                />
              </div>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Monto fijo que se lleva la agencia como comisión (se suma al precio total)
              </p>
            </div>

            {/* Excursión - Required */}
            <div className="space-y-2">
              <label htmlFor="excursion" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                <FileText className="w-4 h-4 inline-block mr-1.5 text-primary-500" />
                Excursión
                <span className="text-red-500">*</span>
              </label>
              <select
                id="excursion"
                value={reservaFormData.excursion}
                onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                  setReservaFormData({ ...reservaFormData, excursion: e.target.value })
                }
                className="h-11 w-full appearance-none rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 pr-11 text-sm shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800 disabled:bg-gray-100 dark:disabled:bg-gray-700 disabled:cursor-not-allowed"
                required
                disabled={!!editingReserva}
              >
                <option value="">Seleccione una excursión</option>
                {excursions.map((exc) => (
                  <option key={`${exc.id}-${exc.name}`} value={exc.name}>
                    {exc.name}
                  </option>
                ))}
              </select>
              {editingReserva && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No es posible cambiar la excursión una vez creada la reserva
                </p>
              )}
            </div>

            {/* Fecha - Required */}
            <div className="space-y-2">
              <label htmlFor="fecha" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                <Calendar className="w-4 h-4 inline-block mr-1.5 text-primary-500" />
                Fecha
                <span className="text-red-500">*</span>
              </label>
              <Input
                id="fecha"
                type="date"
                name="fecha"
                value={reservaFormData.fecha}
                onChange={(e) => setReservaFormData({ ...reservaFormData, fecha: e.target.value })}
                className="w-full"
                placeholder="Seleccionar fecha"
                autoComplete="off"
              />
            </div>

            {/* Adultos y Niños */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label htmlFor="adultos" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  <Users className="w-4 h-4 inline-block mr-1.5 text-primary-500" />
                  Adultos
                  <span className="text-red-500">*</span>
                </label>
                <Input
                  id="adultos"
                  type="number"
                  name="adultos"
                  value={reservaFormData.adultos}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setReservaFormData({ ...reservaFormData, adultos: e.target.value })
                  }
                  min="1"
                  placeholder="1"
                  className="w-full"
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="ninios" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  <User className="w-4 h-4 inline-block mr-1.5 text-primary-500" />
                  Niños
                </label>
                <Input
                  id="ninios"
                  type="number"
                  name="ninios"
                  value={reservaFormData.ninios}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setReservaFormData({ ...reservaFormData, ninios: e.target.value })
                  }
                  min="0"
                  placeholder="0"
                  className="w-full"
                />
              </div>
            </div>

            {/* Seguro de viaje (checkbox) */}
            <div className="space-y-2">
              <div className="flex items-center">
                <input
                  id="incluyeSeguro"
                  type="checkbox"
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  checked={reservaFormData.incluyeSeguro}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setReservaFormData({ ...reservaFormData, incluyeSeguro: e.target.checked })
                  }
                />
                <label htmlFor="incluyeSeguro" className="ml-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Incluye seguro de viaje ($5 por persona)
                </label>
                {reservaFormData.incluyeSeguro && calculatedPricePreview && (
                  <span className="ml-2 text-xs text-green-600 dark:text-green-400">
                    +${(5 * (parseInt(reservaFormData.adultos) + parseInt(reservaFormData.ninios || "0"))).toFixed(2)}
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Agrega $5 por persona (adultos y niños) al costo total
              </p>
            </div>

            {/* Hotel - Required */}
            <div className="space-y-2">
              <label htmlFor="hotel" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                <AlertCircle className="w-4 h-4 inline-block mr-1.5 text-primary-500" />
                Hotel
                <span className="text-red-500">*</span>
              </label>
              <select
                id="hotel"
                value={reservaFormData.hotel}
                onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                  setReservaFormData({ ...reservaFormData, hotel: e.target.value })
                }
                className="h-11 w-full appearance-none rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 pr-11 text-sm shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
                required
              >
                <option value="">Seleccione un hotel</option>
                {hotels.map((hotel, index) => (
                  <option key={index} value={hotel}>
                    {hotel}
                  </option>
                ))}
              </select>
            </div>

            {/* Duplicate check warning component */}
            {!editingReserva && reservaFormData.agencia && reservaFormData.fecha && (
              <DuplicateReservationCheck
                name={
                  reservaFormData.clientEndName || 
                  agencies.find(a => a.id.toString() === reservaFormData.agencia)?.name || 
                  ""
                }
                date={reservaFormData.fecha}
                hotel={reservaFormData.hotel}
                adultos={reservaFormData.adultos ? parseInt(reservaFormData.adultos) : undefined}
                ninios={reservaFormData.ninios ? parseInt(reservaFormData.ninios) : undefined}
                type="b2b"
                onShowDetails={(id) => {
                  // Close current modal, and open the details modal for the selected reservation
                  setIsFormModalOpen(false);
                  // Find the reservation in the existing reservations
                  const reservation = reservas.find(r => r.id === id);
                  if (reservation) {
                    setSelectedReserva(reservation);
                    setIsDetailModalOpen(true);
                  } else {
                    // If not found locally, you might need to fetch it
                    toast.error("No se pudo encontrar la reserva seleccionada");
                  }
                }}
              />
            )}

            {/* Método de pago */}
            <div className="space-y-2">
              <label htmlFor="metodoPago" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                <CreditCard className="w-4 h-4 inline-block mr-1.5 text-primary-500" />
                Método de pago
              </label>
              <select
                id="metodoPago"
                value={reservaFormData.metodoPago}
                onChange={(e) => setReservaFormData(prev => ({
                  ...prev,
                  metodoPago: e.target.value as "efectivo" | "tarjeta" | "transferencia"
                }))}
                className="h-11 w-full appearance-none rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 pr-11 text-sm shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
              >
                <option value="efectivo">Efectivo</option>
                <option value="tarjeta">Tarjeta</option>
                <option value="transferencia">Transferencia</option>
              </select>
            </div>

            {/* Monto facturado */}
            <div className="space-y-2">
              <label htmlFor="montoFacturado" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                <DollarSign className="w-4 h-4 inline-block mr-1.5 text-primary-500" />
                Monto facturado
                <span className="text-red-500">*</span>
              </label>
              <Input
                id="montoFacturado"
                type="number"
                name="montoFacturado"
                value={reservaFormData.montoFacturado}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setReservaFormData({ ...reservaFormData, montoFacturado: e.target.value })
                }
                disabled={!!editingReserva}
                placeholder="0.00"
                step="0.01"
                min="0"
                className="w-full"
              />
              {editingReserva && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Para pagos adicionales, use la opción "Registrar pago" desde la lista de reservas
                </p>
              )}
            </div>

            {/* Justificación de sobrepago - mostramos solo cuando montoFacturado > calculatedPricePreview */}
            {!editingReserva && calculatedPricePreview !== null && 
             parseFloat(reservaFormData.montoFacturado || "0") > calculatedPricePreview && (
              <div className="space-y-2">
                <label htmlFor="justificacionSobrepago" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  <AlertCircle className="w-4 h-4 inline-block mr-1.5 text-warning-500" />
                  Justificación de sobrepago
                  <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="justificacionSobrepago"
                  name="justificacionSobrepago"
                  value={justificacionSobrepago}
                  onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setJustificacionSobrepago(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md border-gray-300 focus:ring-warning-500 focus:border-warning-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                  rows={3}
                  placeholder="Explique por qué el pago inicial excede el precio total de la reserva"
                  required
                />
                <p className="text-sm text-warning-500 dark:text-warning-400">
                  El monto facturado (${parseFloat(reservaFormData.montoFacturado || "0").toFixed(2)}) excede el precio total calculado (${calculatedPricePreview.toFixed(2)}) por ${(parseFloat(reservaFormData.montoFacturado || "0") - calculatedPricePreview).toFixed(2)}. Se requiere una justificación para este sobrepago.
                </p>
              </div>
            )}

            {/* Notas adicionales */}
            <div className="space-y-2">
              <label htmlFor="notas" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                <ClipboardList className="w-4 h-4 inline-block mr-1.5 text-primary-500" />
                Notas adicionales
              </label>
              <textarea
                id="notas"
                name="notas"
                value={reservaFormData.notas}
                onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                  setReservaFormData({ ...reservaFormData, notas: e.target.value })
                }
                className="w-full px-3 py-2 border rounded-md border-gray-300 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                rows={3}
                placeholder="Detalles adicionales sobre la reserva"
              />
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Incluya cualquier información adicional relevante para esta reserva
              </p>
            </div>

            {/* Descuento e Impuestos */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label htmlFor="descuento" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  <TrendingUp className="w-4 h-4 inline-block mr-1.5 text-primary-500" />
                  Descuento (%)
                </label>
                <Input
                  id="descuento"
                  type="number"
                  name="descuento"
                  value={reservaFormData.descuento}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setReservaFormData({ ...reservaFormData, descuento: e.target.value })
                  }
                  min="0"
                  max="100"
                  placeholder="0"
                  className="w-full"
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="taxRate" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  <Tag className="w-4 h-4 inline-block mr-1.5 text-primary-500" />
                  Impuestos (%)
                </label>
                <Input
                  id="taxRate"
                  type="number"
                  name="taxRate"
                  value={reservaFormData.taxRate}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setReservaFormData({ ...reservaFormData, taxRate: e.target.value })
                  }
                  min="0"
                  max="100"
                  placeholder="0"
                  className="w-full"
                />
              </div>
            </div>

            {/* Price preview */}
            {isFormModalOpen && reservaFormData.excursion && (
              <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Precio total estimado:
                  </span>
                  <span className="text-lg font-bold text-primary-600 dark:text-white">
                    {isCalculatingPrice ? (
                      <span className="text-sm text-gray-500 dark:text-gray-400">Calculando...</span>
                    ) : calculatedPricePreview !== null ? (
                      `$${calculatedPricePreview.toFixed(2)}`
                    ) : (
                      <span className="text-sm text-gray-500 dark:text-gray-400">Seleccione excursión y personas</span>
                    )}
                  </span>
                </div>
                {calculatedPricePreview !== null && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Precio con {reservaFormData.descuento || "0"}% de descuento y {reservaFormData.taxRate || "0"}% de impuestos
                    {Number(reservaFormData.agenciaComision) > 0 && (
                      <span className="ml-1 text-blue-600 dark:text-blue-400">
                        (comisión de ${Number(reservaFormData.agenciaComision).toFixed(2)} descontada)
                      </span>
                    )}
                    {hasCustomPrice && (
                      <span className="inline-flex items-center ml-2 text-xs font-medium text-blue-600 dark:text-blue-400">
                        <Tag className="w-3 h-3 mr-1" /> Precio personalizado para esta agencia
                      </span>
                    )}
                  </p>
                )}
              </div>
            )}

            {/* Estado especial (solo en modo edición) */}
            {editingReserva && (
              <div className="space-y-2 mt-2">
                <label htmlFor="estado" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  <FileCheck className="w-4 h-4 inline-block mr-1.5 text-primary-500" />
                  Estado especial
                </label>
                <select
                  id="estado"
                  value={reservaFormData.estado}
                  onChange={(e) => setReservaFormData(prev => ({
                    ...prev,
                    estado: e.target.value
                  }))}
                  className="h-11 w-full appearance-none rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 pr-11 text-sm shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
                >
                  <option value="">Mantener estado actual</option>
                  <option 
                    value="no_show"
                    disabled={editingReserva?.estado === "completed"}
                  >
                    No Show {editingReserva?.estado === "completed" ? '(No disponible para reservas completadas)' : ''}
                  </option>
                  <option 
                    value="completed" 
                    disabled={editingReserva?.saldoPendiente > 0}
                  >
                    Completada {editingReserva?.saldoPendiente > 0 ? '(Requiere pago total)' : ''}
                  </option>
                </select>
                
                {editingReserva?.estado === "completed" ? (
                  <div className="flex items-center mt-1.5 p-2 bg-gray-50 dark:bg-gray-800/50 rounded-md border border-gray-200 dark:border-gray-700">
                    <Info className="w-4 h-4 text-primary-500 mr-2 flex-shrink-0" />
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Las reservas completadas no pueden marcarse como No-Show.
                    </p>
                  </div>
                ) : editingReserva?.saldoPendiente > 0 ? (
                  <div className="flex items-center mt-1.5 p-2 bg-gray-50 dark:bg-gray-800/50 rounded-md border border-gray-200 dark:border-gray-700">
                    <AlertCircle className="w-4 h-4 text-warning-500 mr-2 flex-shrink-0" />
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Saldo pendiente: ${editingReserva.saldoPendiente.toFixed(2)}. 
                      La reserva debe estar totalmente pagada para marcarla como Completada.
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1.5">
                    Solo seleccionar si desea marcar la reserva como No Show o Completada.
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4 mt-2 border-t border-gray-200 dark:border-gray-700">
            <Button variant="outline" type="button" onClick={() => setIsFormModalOpen(false)}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit">
              {editingReserva ? "Actualizar reserva" : "Crear reserva"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Modal para confirmar eliminación */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setReservationIdToDelete(null);
        }}
        title="Confirmación de eliminación"
        size="md"
      >
        <div className="space-y-6 px-2">
          <div className="space-y-4">
            <p className="text-lg font-medium text-gray-900 dark:text-gray-100">
              ¿Está seguro de eliminar esta reserva?
            </p>
            
            <div className="space-y-3">
              <p className="text-sm font-medium text-red-600 dark:text-red-400">
                Esta acción eliminará permanentemente todos los datos asociados a la reserva, incluyendo:
              </p>
              
              <ul className="list-disc list-inside text-sm text-gray-700 dark:text-gray-300 ml-4 space-y-1">
                <li>Detalles de la reserva y excursión</li>
                <li>Registro de pagos realizados</li>
                <li>Historial de cambios y notas</li>
                <li>Cualquier otro dato relacionado</li>
              </ul>
            </div>

            <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/20 rounded-lg">
              <p className="text-sm font-medium text-red-600 dark:text-red-400">
                Esta acción no se puede deshacer.
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteModalOpen(false);
                setReservationIdToDelete(null);
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={async () => {
                if (reservationIdToDelete !== null) {
                  try {
                    await onDeleteReserva(reservationIdToDelete);
                    toast.success("Reserva eliminada exitosamente");
                    setIsDeleteModalOpen(false);
                    setReservationIdToDelete(null);
                  } catch (error) {
                    toast.error("Error al eliminar la reserva");
                  }
                }
              }}
            >
              Sí, eliminar reserva
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal para registrar pago adicional */}
      <Modal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        title="Registrar pago adicional"
      >
        <div className="space-y-4">
          <div>
            <label htmlFor="paymentAmount" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Monto del pago
            </label>
            <input
              id="paymentAmount"
              type="number"
              value={paymentAmount}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setPaymentAmount(e.target.value)}
              className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-brand-500 focus:border-brand-500"
              required
            />
            {selectedReservaForPayment && (
              <p className="text-xs text-gray-500 mt-1">
                Saldo pendiente: ${selectedReservaForPayment.saldoPendiente ? selectedReservaForPayment.saldoPendiente.toFixed(2) : '0.00'}
              </p>
            )}
          </div>
          <div>
            <label htmlFor="paymentMethod" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Método de pago
            </label>
            <select
              id="paymentMethod"
              value={paymentMethod}
              onChange={(e: ChangeEvent<HTMLSelectElement>) => setPaymentMethod(e.target.value as "efectivo" | "tarjeta" | "transferencia")}
              className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-brand-500 focus:border-brand-500"
            >
              <option value="efectivo">Efectivo</option>
              <option value="tarjeta">Tarjeta</option>
              <option value="transferencia">Transferencia</option>
            </select>
          </div>
          
          {/* Conditional rendering of justification field */}
          {Number(paymentAmount) > (selectedReservaForPayment?.saldoPendiente || 0) && (
            <div>
              <label htmlFor="justificacionExtra" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Justificación del pago extra <span className="text-red-500">*</span>
              </label>
              <textarea
                id="justificacionExtra"
                value={justificacionExtra}
                onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setJustificacionExtra(e.target.value)}
                className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-brand-500 focus:border-brand-500"
                placeholder="Indique la razón del pago adicional al saldo pendiente"
                rows={3}
                required
              />
              <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                El monto excede el saldo pendiente por ${(Number(paymentAmount) - (selectedReservaForPayment?.saldoPendiente || 0)).toFixed(2)}. Se requiere justificación.
              </p>
            </div>
          )}
          
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setIsPaymentModalOpen(false)}>
              Cancelar
            </Button>
            <Button 
              variant="primary" 
              onClick={handleRegisterPayment} 
              disabled={Number(paymentAmount) > (selectedReservaForPayment?.saldoPendiente || 0) && !justificacionExtra.trim()}
            >
              Confirmar pago
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal de detalles */}
      {isDetailModalOpen && selectedReserva && (
        <ReservaDetailsModal
          isOpen={isDetailModalOpen}
          onClose={() => setIsDetailModalOpen(false)}
          reserva={selectedReserva}
          type="b2b"
          noShows={noShows}
          userRole={userRole}
          onRegisterNoShow={handleRegisterNoShow}
          onDeleteNoShow={handleDeleteNoShow}
          onNoShowChange={refreshNoShows}
        />
      )}

      {/* Agregar el modal al final del componente */}
      <CreateAgencyModal
        isOpen={isNewAgencyModalOpen}
        onClose={() => setIsNewAgencyModalOpen(false)}
        onAddAgency={createAgency}
        onSuccess={(newAgency) => {
          setAgencies((prev) => [...prev, newAgency]);
          setReservaFormData((prev) => ({
            ...prev,
            agencia: newAgency.id.toString(),
          }));
        }}
      />
    </div>
  );
}

function getStatusBadgeColor(status: string): BadgeColor {
  switch (status) {
    case "pending_payment":
      return "primary";
    case "partial_payment":
      return "info";
    case "paid":
      return "success";
    case "no_show":
      return "warning";
    case "completed":
      return "dark";
    default:
      return "light";
  }
}